import type {
	ClaimFilterDto,
	OrderClaimListResponseDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useDateFormat } from "Infrastructure/Utilities/dateFormat";
import { defaultTableConfig } from "Infrastructure/Utilities/defaultTableConfig";
import { useClaimsList } from "Order/Modules/Claim/Api/Query/getClaimsListQuery";
import { claimStatusChipColor } from "Order/Modules/Claim/Helpers/UI/statusColors";
import { OrderPaths } from "Order/Routes/Routing";
import { PartnerPaths } from "Partner/Routes/Routing";
import { Edit, Link as LinkIcon } from "@mui/icons-material";
import { Box, Chip, IconButton, Stack, Typography } from "@mui/material";
import {
	createMRTColumnHelper,
	type MRT_DensityState,
	type MRT_Row,
} from "material-react-table";
import { Link, useNavigate } from "react-router";

type TableAction = {
	action: () => void;
	icon: JSX.Element;
};

type ClaimTableRow = OrderClaimListResponseDto & {
	actions?: TableAction[];
};

const {
	claimTable: claimTableResource,
	claimShared: sharedResource,
	claimSelectOptions,
} = Resources.base.claim;

const {
	detail: { info: orderTableResource },
} = Resources.base.order;

export const useClaimTable = (filter: ClaimFilterDto) => {
	const { t } = useResource();
	const columnHelper = createMRTColumnHelper<ClaimTableRow>();
	const { data, isLoading } = useClaimsList(filter);
	const navigate = useNavigate();
	const { formatDate } = useDateFormat();

	const tableData = (data ?? []).map((claim) => ({
		...claim,
		actions: [
			{
				action: () => navigate(OrderPaths.Claim.path(claim.orderId)),
				icon: <Edit fontSize="small" />,
			},
		],
	}));

	const columns = [
		columnHelper.accessor((row) => row.dateCreated, {
			id: "dateCreated",
			header: t(claimTableResource.dateCreated),
			maxSize: 150,
			grow: 0,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Typography variant="tableCell">
					{row.original.dateCreated
						? formatDate(row.original.dateCreated)
						: "-"}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.customerName, {
			id: "customerName",
			header: t(claimTableResource.customer),
			maxSize: 150,
			grow: 0,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Stack justifyContent={"center"}>
					<Typography variant="tableCell" fontWeight={700}>
						{row.original?.customerName ?? "-"}
					</Typography>
					<Typography variant="tableCell" color="textSecondary">
						{row.original?.customerEmail ?? "-"}
					</Typography>
				</Stack>
			),
		}),
		columnHelper.accessor((row) => row.partner, {
			id: "partner",
			header: t(claimTableResource.partner),
			maxSize: 150,
			grow: 0,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Stack
					justifyContent={"center"}
					onClick={() =>
						navigate(PartnerPaths.Detail.path(row.original.partner.id))
					}
					sx={{
						cursor: "pointer",
						transition: "all 0.2s ease",
						":hover": { textDecoration: "underline" },
					}}
				>
					<Typography
						variant="tableCell"
						fontWeight={700}
					>{`${row.original.partner.name} ${row.original.partner.middleName && `${row.original.partner.middleName} `}${row.original.partner.surname}`}</Typography>
					<Typography variant="tableCell" color="textSecondary">
						{row.original.partner.email}
					</Typography>
				</Stack>
			),
		}),
		columnHelper.accessor((row) => row.orderId, {
			id: "orderId",
			header: t(claimTableResource.orderId),
			maxSize: 60,
			grow: 0,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Stack
					direction="row"
					columnGap={1}
					alignItems="center"
					minHeight="40px"
				>
					<Typography variant="tableCell">{row.original.orderId}</Typography>
					<Box sx={{ transition: "all 0.2s ease", ":hover": { opacity: 0.7 } }}>
						<Link to={OrderPaths.Claim.path(row.original.orderId)}>
							<Typography color="secondary">
								<LinkIcon fontSize="small" />
							</Typography>
						</Link>
					</Box>
				</Stack>
			),
		}),
		columnHelper.accessor((row) => row.status, {
			id: "status",
			header: t(claimTableResource.status),
			maxSize: 60,
			grow: 0,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Chip
					size="small"
					variant="outlined"
					color={claimStatusChipColor[row.original.status]}
					label={t(claimSelectOptions.claimStatus[row.original.status])}
				/>
			),
		}),
		columnHelper.accessor((row) => row.type, {
			id: "type",
			header: t(claimTableResource.type),
			maxSize: 150,
			grow: 0,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Typography variant="tableCell">{row.original.type}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.finalSolution, {
			id: "finalSolution",
			header: t(claimTableResource.finalSolution),
			maxSize: 150,
			grow: 0,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Typography variant="tableCell">
					{row.original.finalSolution
						? t(claimSelectOptions.claimSolution[row.original.finalSolution])
						: ""}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.dateNextAction, {
			id: "dateNextAction",
			header: t(claimTableResource.dateNextAction),
			maxSize: 150,
			grow: 0,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Typography variant="tableCell">
					{row.original.dateNextAction
						? formatDate(row.original.dateNextAction)
						: "-"}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.dateUpdated, {
			id: "dateUpdated",
			header: t(claimTableResource.dateUpdated),
			maxSize: 150,
			grow: 0,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Typography variant="tableCell">
					{row.original.dateUpdated
						? formatDate(row.original.dateUpdated)
						: "-"}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.dateResolved, {
			id: "dateResolved",
			header: t(claimTableResource.dateResolved),
			maxSize: 150,
			grow: 0,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Typography variant="tableCell">
					{row.original.dateResolved
						? formatDate(row.original.dateResolved)
						: "-"}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.description, {
			id: "description",
			header: t(claimTableResource.description),
			maxSize: 150,
			grow: 0,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Box maxWidth={"180px"} overflow={"hidden"}>
					<Typography
						variant="tableCell"
						sx={{
							whiteSpace: "nowrap",
							textOverflow: "ellipsis",
							overflow: "hidden",
						}}
					>
						{row.original.description?.replace(/<\/?[^>]+(>|$)/g, "")}
					</Typography>
				</Box>
			),
		}),
		// Full user name
		columnHelper.accessor((row) => row.userFirstName, {
			id: "userFirstName",
			header: t(orderTableResource.manager),
			maxSize: 150,
			grow: 0,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Typography variant="tableCell">
					{row.original?.userFirstName && row.original?.userLastName
						? `${row.original?.userFirstName} ${row.original?.userLastName}`
						: "-"}
				</Typography>
			),
		}),
		{
			id: "actions",
			header: t(sharedResource.actions),
			maxSize: 60,
			grow: 0,
			enableColumnActions: false,
			Cell: ({ row }: { row: MRT_Row<ClaimTableRow> }) => (
				<>
					{row.original.actions?.map((action, index) => (
						<IconButton
							key={index}
							onClick={action.action}
							size="small"
							sx={{ backgroundColor: "none" }}
						>
							{action.icon}
						</IconButton>
					))}
				</>
			),
		},
	];

	const tableOptions = {
		...defaultTableConfig,
		columns,
		data: tableData,
		state: {
			showSkeletons: isLoading,
			isLoading,
		},
		initialState: {
			density: "comfortable" as MRT_DensityState,
			columnVisibility: {
				dateResolved: false,
				dateUpdated: false,
				description: false,
			},
		},
		muiTableBodyRowProps: ({ row }) => ({
			onDoubleClick: () => {
				const orderID = row.original.orderId;
				navigate(OrderPaths.Claim.path(orderID));
			},
		}),
	};

	return tableOptions;
};
